import { makeStyles, mergeClasses, shorthands, Spinner, Text, tokens } from '@fluentui/react-components';
import { Signature } from '@gkn-ili/views/dist/Signature';
import React from 'react';
import { AuthenticatedProps, makeAuthenticatedComponent } from '../context/authentication';
import { Checkmark32Regular, Signature32Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  root: {
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorBackgroundOverlay),
    backgroundColor: 'white',
    cursor: 'pointer',
    height: '64px',
    width: '160px',
    marginBlock: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalXXS,
    position: 'relative',
    ':hover': {
      '& svg': {
        transform: 'scale(110%) translate(-50%, -50%)',
      },
    },
    ':focus-visible': {
      ...shorthands.border('1px', 'solid', tokens.colorBrandBackground),
      boxShadow: tokens.shadow2Brand,
      outline: 'none',
    },
  },
  disabled: {
    backgroundColor: tokens.colorNeutralBackground2,
    cursor: 'not-allowed',
    ':hover': {
      '& svg': {
        transform: 'scale(100%) translate(-50%, -50%)',
      },
    },
  },
  signed: {
    background: tokens.colorNeutralBackground2,
  },
  smallText: {
    display: 'block',
    margin: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground2,
  },
  mainText: {
    display: 'block',
    textAlign: 'center',
    width: '100%',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorBrandForeground1,
  },
  icon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    ...shorthands.transition('transform', '0.2s'),
  },
  checkIcon: {
    color: tokens.colorBackgroundOverlay,
  },
  iconDisabled: {
    color: tokens.colorBackgroundOverlay,
  },
});

function appSignatureField(
  props: AuthenticatedProps<{
    signature?: Signature | null;
    clickOnce?: boolean;
    disabled?: boolean;
    loading?: boolean;
    onSign?: (userId: string, employeeCode: string, date: Date) => Promise<void>;
  }>,
): JSX.Element {
  const styles = useStyles();
  const isLoading = !!props.loading;
  const isEnabled = !props.disabled && !props.loading;

  async function onClick() {
    if (props.signature || !props.onSign) {
      return;
    }
    await props.onSign(props.authentication.user.id, props.authentication.user.id, new Date());
  }

  const statusText = isLoading ? 'Signing' : props.signature ? 'Valid' : isEnabled ? 'Click to sign' : '';
  const statusIcon = isLoading ? (
    <Spinner className={styles.icon} />
  ) : props.signature ? (
    <Checkmark32Regular className={mergeClasses(styles.icon, styles.checkIcon)} />
  ) : (
    <Signature32Regular className={mergeClasses(styles.icon, !isEnabled && styles.iconDisabled)} />
  );

  return (
    <div
      tabIndex={0}
      className={mergeClasses(styles.root, props.signature ? styles.signed : undefined, !isEnabled && styles.disabled)}
      onClick={onClick}
    >
      <Text className={styles.smallText}>{statusText}</Text>
      <Text className={styles.mainText}>{props.signature?.employeeCode}</Text>
      <Text className={styles.smallText}>{props.signature?.date.toLocaleString()}</Text>
      {statusIcon}
    </div>
  );
}

const AppSignatureField = makeAuthenticatedComponent(appSignatureField);
export default AppSignatureField;
